/**
 * ERD column-handle ids — the single source of truth.
 *
 * Both DatabaseTableNode (which RENDERS per-column connection handles) and the
 * FK-edge derivation (which REFERENCES those handle ids on edges) import these
 * helpers. Because the render side and the reference side share one function,
 * a referenced handle id can never drift from a rendered one — which is exactly
 * the mismatch that makes React Flow silently drop an edge.
 */

/** Stable, collision-free-per-node id fragment for a column name. */
export function sanitizeColId(name: string): string {
  return name.replace(/[^A-Za-z0-9_]/g, '_')
}

/** Deterministic handle id for a column's connection point on a given side. */
export function columnHandleId(columnName: string, side: 'left' | 'right'): string {
  return `col-${sanitizeColId(columnName)}-${side}`
}

/**
 * The full set of column handle ids a table node renders — left AND right for
 * every column. DatabaseTableNode renders exactly this set, and the FK-edge
 * derivation only references ids from it, so coverage is guaranteed.
 */
export function columnHandleIds(columnNames: string[]): string[] {
  return columnNames.flatMap((name) => [columnHandleId(name, 'left'), columnHandleId(name, 'right')])
}
